import type * as React from 'react';
import type { VanillaLinkProps } from '../Link/index.ts';
import { Button as BaseButton } from '@base-ui/react/button';
import clsx from 'clsx';
import { LayoutGroup } from 'motion/react';
import { createContext, use, useId } from 'react';

const SidebarContext = createContext<{ onClickItem?: () => void }>({});

export type SidebarProps = React.ComponentPropsWithRef<'nav'> & { onClickItem?: () => void };

export function Sidebar({ className, onClickItem, ...props }: SidebarProps) {
  return (
    <SidebarContext value={{ onClickItem }}>
      <nav {...props} className={clsx(className, 'flex h-full min-h-0 flex-col')} />
    </SidebarContext>
  );
}

export function SidebarHeader({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-b border-gray-950/10 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  );
}

export function SidebarBody({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8',
        'scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent hover:scrollbar-thumb-black/40 dark:hover:scrollbar-thumb-white/40'
      )}
    />
  );
}

export function SidebarFooter({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-gray-950/10 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5'
      )}
    />
  );
}

export function SidebarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} data-slot="section" className={clsx(className, 'flex flex-col gap-1')} />
    </LayoutGroup>
  );
}

export function SidebarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'hr'>) {
  return (
    <hr
      {...props}
      className={clsx(className, 'my-4 border-t border-gray-950/5 lg:-mx-4 dark:border-white/5')}
    />
  );
}

export function SidebarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />;
}

export function SidebarHeading({ className, ...props }: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3
      {...props}
      className={clsx(className, 'mb-1 px-2 text-xs/6 text-gray-500 dark:text-gray-400')}
    />
  );
}

export function SidebarItem({
  className,
  children,
  current,
  depth = 0,
  onClick,
  renderLink,
  ref,
  ...props
}: {
  children?: React.ReactNode;
  className?: string;
  current?: boolean;
  depth?: number;
  onClick?: React.MouseEventHandler<HTMLElement>;
  ref?: React.Ref<HTMLElement>;
  renderLink?: (props: VanillaLinkProps) => React.ReactNode;
  selected?: boolean;
} & Omit<React.ComponentPropsWithoutRef<typeof BaseButton>, 'className' | 'onClick' | 'render'>) {
  const { onClickItem } = use(SidebarContext);

  const classes = clsx(
    // Base
    'group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-gray-700 sm:py-2 sm:text-sm/5',

    // Icon
    '[&_[data-slot=icon]>*]:text-gray-500',

    // Hover
    'hover:bg-gray-950/5 hover:[&_[data-slot=icon]>*]:text-gray-950',

    // Active
    'active:bg-gray-950/5 active:[&_[data-slot=icon]>*]:text-gray-950',

    // Current
    'data-current:bg-gray-950/5 data-current:text-gray-950 data-current:[&_[data-slot=icon]>*]:text-gray-950',

    // Dark mode
    'dark:text-gray-400 dark:[&_[data-slot=icon]>*]:text-gray-400',
    'dark:hover:bg-white/5 dark:hover:[&_[data-slot=icon]>*]:text-white',
    'dark:active:bg-white/5 dark:active:[&_[data-slot=icon]>*]:text-white',
    'dark:data-current:bg-white/10 dark:data-current:text-white dark:data-current:[&_[data-slot=icon]>*]:text-white',

    // Disabled
    'data-disabled:opacity-50',

    // Focus
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500'
  );

  return (
    <span className={clsx(className, 'relative flex')}>
      {depth > 0 && (
        <span className="w-8 before:absolute before:-top-0.5 before:-bottom-0.5 before:left-4 before:w-px before:bg-gray-300 dark:before:bg-gray-500" />
      )}

      {renderLink ? (
        renderLink({
          children,
          className: classes,
          'data-current': current ? 'true' : undefined,
          onClick: event => {
            onClick?.(event);
            onClickItem?.();
          },
          plain: true,
          ref: ref as React.Ref<HTMLAnchorElement>
        })
      ) : (
        <BaseButton
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          onClick={event => {
            onClick?.(event);
            onClickItem?.();
          }}
          ref={ref}
        >
          {children}
        </BaseButton>
      )}
    </span>
  );
}

export function SidebarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />;
}

export function SidebarAvatar({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx('-m-0.5 *:size-7 sm:*:size-6')} data-slot="avatar" />;
}

export function SidebarIcon({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        // Leading icon/icon-only
        '*:size-6 *:shrink-0 sm:*:size-5',
        // Trailing icon
        'last:ml-auto last:*:size-5 sm:last:*:size-4'
      )}
      data-slot="icon"
    />
  );
}
